/**
 * CustomersPage
 * Main customer list page with search, filters, and bulk actions
 */

import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Search,
  Plus,
  Filter,
  Download,
  Upload,
  X,
  Archive,
  Trash2,
  LayoutGrid,
  LayoutList,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Eye,
  Edit,
  Bell,
  UserCheck,
} from 'lucide-react'
import { useCustomerStore, useActiveFilterCount } from '@/store/customerStore'
import { toast } from '@/store/toastStore'
import CustomerTable, { CustomerTableSkeleton } from '@/components/customers/CustomerTable'
import CustomerFilters from '@/components/customers/CustomerFilters'
import CustomerModal from '@/components/customers/CustomerModal'
import AssignCustomerModal from '@/components/customers/AssignCustomerModal'
import { useAuth } from '@/contexts/AuthContext'
import { getReminderService } from '@/services/reminders'

export default function CustomersPage() {
  const { hasPermission } = useAuth()
  const canCreate = hasPermission('txh.client.create')
  const canUpdate = hasPermission('txh.client.update')
  const canDelete = hasPermission('txh.client.delete')
  const canAssign = canUpdate && hasPermission('txh.user.read')
  const canExport = hasPermission('txh.client.read') && hasPermission('txh.client.export')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [showRemindModal, setShowRemindModal] = useState(false)
  const [reminderTitle, setReminderTitle] = useState('')
  const [reminderDueDate, setReminderDueDate] = useState('')
  const [reminderPriority, setReminderPriority] = useState<'low' | 'medium' | 'high'>('medium')
  const [reminderNotes, setReminderNotes] = useState('')
  const [isCreatingReminders, setIsCreatingReminders] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [showMoreOptions, setShowMoreOptions] = useState(false)

  const {
    isLoading,
    loadCustomers,
    setSearchQuery,
    selectedIds,
    clearSelection,
    isFilterPanelOpen,
    toggleFilterPanel,
    viewMode,
    setViewMode,
    page,
    pageSize,
    setPage,
    setPageSize,
    getPaginatedCustomers,
    bulkArchive,
    bulkDelete,
  } = useCustomerStore()

  const activeFilterCount = useActiveFilterCount()

  // Load customers on mount (mock or real, depending on API mode)
  useEffect(() => {
    loadCustomers()
  }, [loadCustomers])

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput, setSearchQuery])

  // Open modal if query param is set
  useEffect(() => {
    if (canCreate && searchParams.get('new') === 'true') {
      setShowCreateModal(true)
    }
  }, [searchParams])

  const { items: customers, total, totalPages } = getPaginatedCustomers()

  const handleCustomerClick = (id: string) => {
    navigate(`/customers/${id}`)
  }

  const handleCreateSuccess = (customerId: string) => {
    if (!canCreate) return
    setShowCreateModal(false)
    navigate(`/customers/${customerId}`)
  }

  const handleBulkArchive = async () => {
    if (!canUpdate) return
    if (window.confirm(`Are you sure you want to archive ${selectedIds.length} customer(s)?`)) {
      const count = selectedIds.length
      try {
        await bulkArchive(selectedIds)
        toast.success(
          `${count} customer${count > 1 ? 's' : ''} archived`,
          'You can restore them from the archived filter.'
        )
      } catch {
        toast.error('Couldn’t archive customers', 'Please try again in a moment.')
      }
    }
  }

  const handleBulkDelete = async () => {
    if (!canDelete) return
    if (window.confirm(`Are you sure you want to delete ${selectedIds.length} customer(s)? This action cannot be undone.`)) {
      const count = selectedIds.length
      try {
        await bulkDelete(selectedIds)
        toast.success(`${count} customer${count > 1 ? 's' : ''} deleted`)
      } catch {
        toast.error('Couldn’t delete customers', 'Please try again in a moment.')
      }
    }
  }


  const handleBulkRemind = async () => {
    if (!reminderTitle.trim() || !reminderDueDate) {
      toast.error('Required fields missing', 'Please provide a title and due date')
      return
    }
    setIsCreatingReminders(true)
    try {
      const reminderService = getReminderService()
      await Promise.all(
        selectedIds.map((customerId) =>
          reminderService.create({
            entityType: 'customer',
            entityId: customerId,
            title: reminderTitle.trim(),
            dueDate: new Date(reminderDueDate).toISOString(),
            priority: reminderPriority,
            description: reminderNotes.trim() || undefined,
          })
        )
      )
      toast.success(
        'Reminders created',
        `Scheduled reminders for ${selectedIds.length} customer(s)`
      )
      setShowRemindModal(false)
      setReminderTitle('')
      setReminderDueDate('')
      setReminderNotes('')
      clearSelection()
    } catch (err) {
      toast.error('Failed to create reminders', err instanceof Error ? err.message : 'Please try again')
    } finally {
      setIsCreatingReminders(false)
    }
  }

  const handleBulkExport = () => {
    if (!canExport) return
    const selectedCustomers = customers.filter((c) => selectedIds.includes(c.id))
    const headers = ['Name', 'PAN', 'Email', 'Mobile', 'Status', 'Assigned CA', 'Income Range']
    const rows = selectedCustomers.map((c) => [
      c.fullName,
      c.pan,
      c.emailPrimary,
      c.mobilePrimary,
      c.status,
      c.assignedCAName || '',
      c.annualIncomeRange,
    ])

    const csv = [headers.join(','), ...rows.map((r) => r.map((cell) => `"${cell}"`).join(','))].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `customers-selected-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
    toast.success('Export completed', `Exported ${selectedCustomers.length} customer(s) to CSV`)
  }

  const handleExport = () => {
    if (!canExport) return
    // Simple CSV export
    const headers = ['Name', 'PAN', 'Email', 'Mobile', 'Status', 'Assigned CA', 'Income Range']
    const rows = customers.map((c) => [
      c.fullName,
      c.pan,
      c.emailPrimary,
      c.mobilePrimary,
      c.status,
      c.assignedCAName || '',
      c.annualIncomeRange,
    ])

    const csv = [headers.join(','), ...rows.map((r) => r.map((cell) => `"${cell}"`).join(','))].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `customers-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleViewDetails = () => {
    if (selectedIds.length === 1) {
      navigate(`/customers/${selectedIds[0]}`)
      setShowMoreOptions(false)
    }
  }

  const handleEdit = () => {
    if (canUpdate && selectedIds.length === 1) {
      navigate(`/customers/${selectedIds[0]}?edit=true`)
      setShowMoreOptions(false)
    }
  }

  const handleArchive = async () => {
    if (canUpdate && selectedIds.length > 0) {
      await handleBulkArchive()
      setShowMoreOptions(false)
    }
  }

  const handleDelete = async () => {
    if (canDelete && selectedIds.length > 0) {
      await handleBulkDelete()
      setShowMoreOptions(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Customers</h1>
          <p className="text-sm text-text-secondary mt-1">
            Manage individual taxpayer customers
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canCreate && (
            <button className="btn btn-outline btn-sm hidden sm:flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Import
            </button>
          )}
          {canExport && (
            <button onClick={handleExport} className="btn btn-outline btn-sm flex items-center gap-2">
              <Download className="w-4 h-4" />
              Export
            </button>
          )}

          {canCreate && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn btn-primary btn-sm flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Customer
            </button>
          )}

          {/* More Options Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowMoreOptions(!showMoreOptions)}
              onBlur={() => setTimeout(() => setShowMoreOptions(false), 200)}
              disabled={selectedIds.length === 0}
              className="btn btn-outline btn-sm flex items-center gap-2"
            >
              <MoreHorizontal className="w-4 h-4" />
              More Options
            </button>

            {showMoreOptions && selectedIds.length > 0 && (
              <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-surface-dark rounded-xl shadow-xl border border-surface-overlay z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="py-2">
                  {/* View Details */}
                  <button
                    onClick={handleViewDetails}
                    disabled={selectedIds.length !== 1}
                    className="w-full px-4 py-2.5 text-left text-sm font-medium text-text-primary hover:bg-primary-50 dark:hover:bg-primary-900/20 flex items-center gap-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                  >
                    <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                      <Eye className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <div className="text-text-primary">View Details</div>
                      {selectedIds.length !== 1 && (
                        <div className="text-xs text-text-tertiary mt-0.5">Select 1 customer</div>
                      )}
                    </div>
                  </button>

                  {/* Edit */}
                  {canUpdate && (
                    <button
                      onClick={handleEdit}
                      disabled={selectedIds.length !== 1}
                      className="w-full px-4 py-2.5 text-left text-sm font-medium text-text-primary hover:bg-primary-50 dark:hover:bg-primary-900/20 flex items-center gap-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    >
                      <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                        <Edit className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div className="flex-1">
                        <div className="text-text-primary">Edit</div>
                        {selectedIds.length !== 1 && (
                          <div className="text-xs text-text-tertiary mt-0.5">Select 1 customer</div>
                        )}
                      </div>
                    </button>
                  )}

                  {/* Divider */}
                  {(canUpdate || canDelete) && <div className="my-2 border-t border-surface-overlay"></div>}

                  {/* Archive */}
                  {canUpdate && (
                    <button
                    onClick={handleArchive}
                    className="w-full px-4 py-2.5 text-left text-sm font-medium text-text-primary hover:bg-orange-50 dark:hover:bg-orange-900/20 flex items-center gap-3 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0">
                      <Archive className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div className="flex-1">
                      <div className="text-text-primary">Archive</div>
                      <div className="text-xs text-text-tertiary mt-0.5">{selectedIds.length} selected</div>
                    </div>
                    </button>
                  )}

                  {/* Delete */}
                  {canDelete && (
                    <button
                    onClick={handleDelete}
                    className="w-full px-4 py-2.5 text-left text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-3 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                      <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                    </div>
                    <div className="flex-1">
                      <div>Delete</div>
                      <div className="text-xs text-red-500/70 dark:text-red-400/70 mt-0.5">{selectedIds.length} selected</div>
                    </div>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Search and Filters Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-secondary" />
          <input
            type="text"
            placeholder="Search customers by name, PAN, email, or phone..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="input input-bordered pl-10 w-full"
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 btn btn-ghost btn-circle btn-xs"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleFilterPanel}
            className={`btn btn-outline flex items-center gap-2 ${isFilterPanelOpen ? 'btn-primary' : ''}`}
          >
            <Filter className="w-4 h-4" />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-primary-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                {activeFilterCount}
              </span>
            )}
          </button>
          <div className="hidden sm:flex items-center border border-surface-overlay rounded-lg">
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-l-lg transition-colors ${viewMode === 'table' ? 'bg-surface-overlay' : 'hover:bg-surface-overlay/50'}`}
              title="Table view"
            >
              <LayoutList className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('card')}
              className={`p-2 rounded-r-lg transition-colors ${viewMode === 'card' ? 'bg-surface-overlay' : 'hover:bg-surface-overlay/50'}`}
              title="Card view"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
          <label className="hidden sm:flex items-center gap-2 text-sm text-text-secondary cursor-pointer select-none">
            <input
              type="checkbox"
              className="checkbox checkbox-sm"
              checked={false}
              onChange={() => { /* archived toggle — filters will be wired to store */ }}
            />
            Show archived
          </label>
        </div>
      </div>

      {/* Filters Panel */}
      <CustomerFilters />

      {/* Bulk Actions Bar */}
      {selectedIds.length > 0 && (canAssign || canUpdate || canDelete) && (
        <div className="card card-elevated px-5 py-4 flex flex-wrap items-center justify-between gap-3 bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800 animate-in slide-in-from-top-2 duration-200">
          <span className="text-sm font-medium text-text-primary">
            {selectedIds.length} customer{selectedIds.length > 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            {canAssign && (
              <button
                onClick={() => setShowAssignModal(true)}
                className="btn btn-ghost btn-sm flex items-center gap-1"
              >
                <UserCheck className="w-4 h-4" />
                Assign Team
              </button>
            )}
            <button
              onClick={() => setShowRemindModal(true)}
              className="btn btn-ghost btn-sm flex items-center gap-1"
            >
              <Bell className="w-4 h-4" />
              Remind
            </button>
            {canExport && (
              <button
                onClick={handleBulkExport}
                className="btn btn-ghost btn-sm flex items-center gap-1"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            )}
            {canUpdate && (
              <button
                onClick={handleBulkArchive}
                className="btn btn-ghost btn-sm flex items-center gap-1"
              >
                <Archive className="w-4 h-4" />
                Archive
              </button>
            )}
            {canDelete && (
              <button
                onClick={handleBulkDelete}
                className="btn btn-ghost btn-sm flex items-center gap-1 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            )}
            <button onClick={clearSelection} className="btn btn-ghost btn-sm btn-circle">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && <CustomerTableSkeleton />}

      {/* Customer Table */}
      {!isLoading && (
        <CustomerTable customers={customers} onCustomerClick={handleCustomerClick} />
      )}

      {/* Pagination */}
      {!isLoading && total > 0 && (
        <div className="card card-elevated p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <p className="text-sm text-text-secondary">
              Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total} customers
            </p>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(parseInt(e.target.value))}
              className="input input-bordered input-sm w-20"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="btn btn-ghost btn-sm"
            >
              First
            </button>
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="btn btn-ghost btn-sm btn-circle"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {/* Page Numbers */}
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number
                if (totalPages <= 5) {
                  pageNum = i + 1
                } else if (page <= 3) {
                  pageNum = i + 1
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i
                } else {
                  pageNum = page - 2 + i
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`btn btn-sm btn-circle ${page === pageNum ? 'btn-primary' : 'btn-ghost'}`}
                  >
                    {pageNum}
                  </button>
                )
              })}
            </div>

            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="btn btn-ghost btn-sm btn-circle"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="btn btn-ghost btn-sm"
            >
              Last
            </button>
          </div>
        </div>
      )}

      {/* Create Customer Modal */}
      {canCreate && (
        <CustomerModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {/* Assign CA / Team Modal */}
      {canAssign && (
        <AssignCustomerModal
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          customerIds={selectedIds}
          onSuccess={() => {
            setShowAssignModal(false)
            clearSelection()
          }}
        />
      )}
      {/* Bulk Remind Modal */}
      {showRemindModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999] animate-in fade-in duration-200">
          <div className="bg-white dark:bg-surface-dark rounded-xl border border-surface-overlay shadow-xl max-w-md w-full p-6 animate-in zoom-in-95 slide-in-from-bottom-2 duration-200 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                <h3 className="text-lg font-semibold text-text-primary">Schedule Bulk Reminder</h3>
              </div>
              <button
                onClick={() => setShowRemindModal(false)}
                className="btn btn-ghost btn-circle btn-sm"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-text-secondary">
              Create a reminder for {selectedIds.length} selected customer{selectedIds.length > 1 ? 's' : ''}:
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Reminder Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={reminderTitle}
                  onChange={(e) => setReminderTitle(e.target.value)}
                  placeholder="e.g. Follow up on Form 16 submission"
                  className="input input-bordered w-full"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Due Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={reminderDueDate}
                    onChange={(e) => setReminderDueDate(e.target.value)}
                    className="input input-bordered w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Priority
                  </label>
                  <select
                    value={reminderPriority}
                    onChange={(e) => setReminderPriority(e.target.value as any)}
                    className="input input-bordered w-full"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Notes (optional)
                </label>
                <textarea
                  value={reminderNotes}
                  onChange={(e) => setReminderNotes(e.target.value)}
                  rows={3}
                  placeholder="Additional context or filing deadline details"
                  className="input input-bordered w-full py-2"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-surface-overlay">
              <button
                type="button"
                onClick={() => setShowRemindModal(false)}
                disabled={isCreatingReminders}
                className="btn btn-outline btn-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBulkRemind}
                disabled={isCreatingReminders || !reminderTitle.trim() || !reminderDueDate}
                className="btn btn-primary btn-sm flex items-center gap-1"
              >
                {isCreatingReminders ? 'Scheduling…' : `Schedule (${selectedIds.length})`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
